<template>
  <!--
    产品网格骨架屏。
    只在「页面已挂载、但数据尚未到达」的空窗期出现（由 ProductGrid 的 loading / showSkeleton 驱动），
    几何刻意对齐真实布局：导航行高度/发丝线对齐 NavBar，卡片列数/间距对齐 ProductGrid，
    因此骨架撤下、真内容顶上时不会发生跳动。
  -->
  <div role="status" aria-busy="true">
    <!-- 骨架对读屏软件无信息量，用视觉隐藏文本播报加载状态 -->
    <p class="sk-sr-only">{{ $t('interface.loading_data') }}</p>

    <!-- 日期导航行骨架（对应 NavBar 的周条行 + 「年月」/「日」按钮） -->
    <div class="sk-nav">
      <div class="sk-nav-inner">
        <!-- 周条占位区：flex:1 + space-between，让 pill 铺满整行（对齐真实周条满宽排布） -->
        <div class="sk-nav-tabs">
          <span
            v-for="(w, i) in navPillWidths"
            :key="'w' + i"
            class="sk sk-pill"
            :style="{ width: w + 'px' }"
          ></span>
        </div>
        <span class="sk sk-pill sk-pill-btn"></span>
        <span class="sk sk-pill sk-pill-btn sk-pill-btn-sm"></span>
      </div>
    </div>

    <!-- 卡片网格骨架 -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="n in cardCount" :key="n" class="sk-card">
        <!-- logo + 名称 / UPVOTE / 发布日期 -->
        <div class="flex items-center px-6 pt-5">
          <span class="sk sk-logo"></span>
          <div class="min-w-0 flex-1">
            <span class="sk sk-bar" style="width: 58%; height: 18px;"></span>
            <span class="sk sk-bar" style="width: 76px; height: 12px; margin-top: 12px;"></span>
            <span class="sk sk-bar" style="width: 92px; height: 12px; margin-top: 8px;"></span>
          </div>
        </div>

        <!-- 简介 / 用途说明 / 标签 -->
        <div class="px-6 py-4">
          <span class="sk sk-bar" style="width: 92%; height: 15px;"></span>
          <span class="sk sk-bar" style="width: 86%; height: 12px; margin-top: 12px;"></span>
          <span class="sk sk-bar" style="width: 64%; height: 12px; margin-top: 8px;"></span>
          <div class="flex flex-wrap gap-2 mt-3">
            <span
              v-for="(w, i) in tagWidths"
              :key="'t' + i"
              class="sk sk-tag"
              :style="{ width: w + 'px' }"
            ></span>
          </div>
        </div>

        <!-- 展示图片区 -->
        <div class="sk sk-image"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductGridSkeleton',
  props: {
    // 骨架卡片数量：默认 6（lg 下两行三列），够铺满首屏又不至于拉长占位
    cardCount: { type: Number, default: 6 },
  },
  data() {
    return {
      // 周条 pill 的示意宽度（px）：模拟「X月X日 - X月X日」不等宽文本，两端对齐铺满整行
      navPillWidths: [92, 104, 96, 110, 88, 100, 94],
      // 标签胶囊宽度（px），数量与真实卡片最多 3 个 tag 对齐
      tagWidths: [52, 44, 60],
    };
  },
};
</script>

<style scoped>
/* 视觉隐藏（仅读屏可读）：不依赖 Tailwind 的 sr-only，避免该工具类未生成时失效 */
.sk-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* —— 占位块基元：暖调底 + 高光扫过 —— */
.sk {
  display: block;
  position: relative;
  overflow: hidden;
  background-color: var(--sk-base, #eae3d4);
  border-radius: 3px;
}

.sk::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transform: translateX(-100%);
  background-image: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.72) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: sk-shimmer 1.5s ease-in-out infinite;
}

@keyframes sk-shimmer {
  100% {
    transform: translateX(100%);
  }
}

/* 尊重系统「减少动态效果」：停掉扫光，保留静态占位 */
@media (prefers-reduced-motion: reduce) {
  .sk::after {
    animation: none;
    display: none;
  }
}

/* —— 导航行骨架：几何对齐 NavBar（py-3 / mb-4 / 92% 宽 / 底部发丝线） —— */
.sk-nav {
  position: relative;
  padding: 0.75rem 0;
  margin-bottom: 1rem;
  border-bottom: 1px solid var(--brass-rule, #e0d8c6);
}

.sk-nav-inner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 92%;
  margin: 0 auto;
}

/* 周条占位区：撑满按钮以外的剩余宽度，两端对齐铺满整行 */
.sk-nav-tabs {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-right: 0.5rem;
  overflow: hidden;
}

/* 与 .date-span 同高（padding 0.4rem + line-height 1.2 ≈ 32px） */
.sk-pill {
  height: 32px;
  border-radius: 0.25rem;
  flex-shrink: 0;
}

.sk-pill-btn {
  width: 68px;
}

.sk-pill-btn-sm {
  width: 44px;
}

/* —— 卡片骨架：纸面/描边/圆角/阴影与 .product-card 一致，仅去掉 hover 反馈 —— */
.sk-card {
  background-color: var(--card-bg, #fbfaf7);
  border: 1px solid var(--brass-rule, #e0d8c6);
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(27, 24, 21, 0.04);
  overflow: hidden;
}

.sk-bar {
  border-radius: 3px;
}

.sk-logo {
  width: 80px;
  height: 80px;
  margin-right: 1rem;
  flex-shrink: 0;
  border-radius: 2px;
}

.sk-tag {
  height: 20px;
  border-radius: 999px;
}

/* 展示图区：与卡片同宽、底部直角（由卡片 overflow:hidden 收角） */
.sk-image {
  height: 190px;
  border-radius: 0;
  margin-top: 4px;
}
</style>
